import React, { useEffect } from 'react';
import ReactDom from 'react-dom';
import propTypes from 'prop-types';
import { CircularProgress } from '@mui/material';
import { X } from 'lucide-react';
import Button from 'tailwind-components/Button/Button';
import LoadingContent from 'components/LoadingContent/LoadingContent';
import { cn } from 'lib/utils';
import { getCompany } from 'helpers/getCompanyVariables';

// oneill's scss $primary-color is darker than tailwind's `primary`; sunshine's is the same color
const LEGACY_TITLE_COLOR =
  getCompany() === 'oneill' ? 'text-primary-legacy' : 'text-primary';

// the legacy breakpoints are 767.98px (tablet portrait), not tailwind's md
const LEGACY_MOBILE =
  'max-[767.98px]:w-[95%] max-[767.98px]:min-w-auto! max-[767.98px]:min-h-auto!';

// `larger` is 70% wide from tablet portrait up, so its 1080px base is never visible
const LEGACY_LARGER_UP =
  'min-[767.98px]:w-[70%] min-[767.98px]:min-w-auto! min-[767.98px]:min-h-auto!';

const legacySizeClasses = {
  auto: `min-w-[450px] min-h-auto ${LEGACY_MOBILE}`,
  small: `w-[450px] ${LEGACY_MOBILE}`,
  medium: `w-[650px] ${LEGACY_MOBILE}`,
  large: `w-[800px] ${LEGACY_MOBILE}`,
  larger: `w-[1080px] ${LEGACY_LARGER_UP} ${LEGACY_MOBILE}`,
  xlarger: `w-[95%] ${LEGACY_MOBILE}`
};

const elevatedSizeClasses = {
  auto: 'w-auto min-w-[min(450px,100%)]',
  small: 'w-[min(450px,100%)]',
  medium: 'w-[min(650px,100%)]',
  large: 'w-[min(800px,100%)]',
  larger: 'w-[min(1080px,100%)]',
  xlarger: 'w-full'
};

// class maps per variant, `default` reproduces the legacy scss modal
const variantStyles = {
  default: {
    overlay:
      'absolute top-0 left-0 z-999 flex h-screen w-screen items-center justify-center overflow-hidden',
    card: 'absolute m-2.5 flex h-auto max-h-[calc(100vh-100px)] flex-col overflow-y-hidden rounded-[15px] bg-white p-5 shadow-[0px_0px_20px_-10px_rgba(0,0,0,0.75)]',
    header: 'flex justify-between items-start! mb-2.5',
    title: `m-0 p-0 text-[20px] font-semibold ${LEGACY_TITLE_COLOR}`,
    dangerTitle: 'm-0 p-0 text-[20px] font-semibold text-semantic-danger',
    closeButton:
      'z-999 border-transparent shadow-none shrink-0 text-grey-light hover:text-grey hover:bg-transparent!',
    content: 'max-h-[calc(100vh-300px)] grow overflow-y-auto',
    contentLoading: 'flex h-full items-center justify-center',
    footer: 'mt-2.5'
  },
  elevated: {
    overlay:
      'fixed inset-0 z-999 flex items-center justify-center overflow-hidden p-4',
    card: 'relative flex max-h-[calc(100vh-4rem)] flex-col gap-4 rounded-2xl bg-white p-5 shadow-[0_24px_60px_-20px_rgba(26,32,79,0.45)]',
    header: 'flex shrink-0 items-start justify-between gap-3',
    title: 'm-0 text-lg font-bold leading-tight text-primary',
    dangerTitle: 'm-0 text-lg font-bold leading-tight text-semantic-danger',
    closeButton:
      'shrink-0 border-transparent text-grey-light shadow-none hover:text-grey',
    content: 'min-h-0 flex-1 overflow-y-auto',
    contentLoading: 'flex items-center justify-center',
    footer: 'shrink-0'
  }
};

// xlarger keeps its 300px floor even against a caller's unlayered `min-height: 0`
function getLegacyMinHeightClass(size, alert) {
  if (alert) return 'min-h-auto';
  if (size === 'xlarger') return 'min-h-[300px]!';
  return '';
}

function getCardSizeClass(isElevated, size) {
  if (isElevated)
    return elevatedSizeClasses[size] ?? elevatedSizeClasses.medium;
  return legacySizeClasses[size];
}

function getTitleClassName(styles, danger, headerTitleClassName) {
  if (headerTitleClassName != null) return headerTitleClassName;
  return danger ? styles.dangerTitle : styles.title;
}

function getHeaderStyle(fullBleedHeader, headerStyle) {
  if (!fullBleedHeader) return headerStyle;
  return {
    ...headerStyle,
    padding: '16px 20px',
    borderRadius: '15px 15px 0 0'
  };
}

/**
 * centered dialog rendered in the #modalPortal node, closed with escape or the
 * header button. the default variant is the legacy modal look, `elevated` is
 * the softer card used by the dashboard notes form
 * @param active - whether the dialog is mounted
 * @param closeModal - closes the dialog
 * @param variant - `default` (legacy look) or `elevated` (fixed overlay, clickable backdrop, h2 title)
 * @param title - heading of the dialog
 * @param children - body of the dialog
 * @param footer - actions pinned under the body, hidden while loading
 * @param size - width preset of the card
 * @param isLoading - replaces the body with a loading indicator
 * @param alert - drops the minimum height of the card
 * @param danger - paints the heading with the danger color
 * @param hideFooter - keeps the footer out even when one is passed
 * @param className - extra classes for the outer overlay wrapper
 * @param showScrollbar - keeps the scrollbar visible when the body overflows
 * @param headerClassName - extra classes for the header row
 * @param headerStyle - inline style for the header row
 * @param fullBleedHeader - removes the card top padding so the header can paint edge to edge
 * @param cardStyle - inline style for the card
 * @param cardDataTour - data-tour value for the card, when a tour step targets it
 * @param headerTitleClassName - replaces the default title classes
 * @param closeButtonClassName - extra classes for the close button
 * @param closeButtonStyle - inline style for the close button
 * @param contentRef - ref forwarded to the scrolling body
 */
export default function Modal({
  active,
  closeModal,
  variant = 'default',
  title,
  children,
  footer,
  size = 'medium',
  isLoading = false,
  alert = false,
  danger = false,
  hideFooter = false,
  className,
  showScrollbar = false,
  headerClassName,
  headerStyle,
  fullBleedHeader = false,
  cardStyle,
  cardDataTour,
  headerTitleClassName,
  closeButtonClassName,
  closeButtonStyle,
  contentRef
}) {
  useEffect(() => {
    if (!active) return undefined;
    function handleKeyDown(e) {
      if (e.key === 'Escape') closeModal();
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [active, closeModal]);

  if (!active) return null;

  const isElevated = variant === 'elevated';
  const styles = variantStyles[isElevated ? 'elevated' : 'default'];
  const TitleTag = isElevated ? 'h2' : 'div';
  const cardMinHeightClass = isElevated
    ? ''
    : getLegacyMinHeightClass(size, alert);
  const cardAriaProps = isElevated
    ? { role: 'dialog', 'aria-modal': 'true' }
    : {};

  return ReactDom.createPortal(
    <div className={cn(styles.overlay, className)}>
      {isElevated ? (
        <button
          type='button'
          aria-label='Close'
          tabIndex={-1}
          onClick={closeModal}
          className='absolute inset-0 cursor-default border-0 bg-black/50 p-0 backdrop-blur-[2px]'
        />
      ) : (
        <div className='h-full w-full bg-black opacity-60' />
      )}
      <div
        {...cardAriaProps}
        {...(cardDataTour ? { 'data-tour': cardDataTour } : {})}
        className={cn(
          styles.card,
          getCardSizeClass(isElevated, size),
          cardMinHeightClass
        )}
        style={{ ...(fullBleedHeader ? { paddingTop: 0 } : {}), ...cardStyle }}
      >
        <div
          className={cn(styles.header, headerClassName)}
          style={getHeaderStyle(fullBleedHeader, headerStyle)}
        >
          <TitleTag
            className={getTitleClassName(styles, danger, headerTitleClassName)}
          >
            {title}
          </TitleTag>
          <Button
            variant='transparent'
            type='button'
            onClick={closeModal}
            aria-label='Close'
            icon={<X className='size-3.5' strokeWidth={2.25} aria-hidden />}
            size='small'
            rounded='medium'
            className={cn(styles.closeButton, closeButtonClassName)}
            style={closeButtonStyle}
          />
        </div>

        <div
          ref={contentRef}
          className={cn(
            styles.content,
            showScrollbar && 'show-scrollbar',
            isLoading && styles.contentLoading
          )}
        >
          {isLoading && !isElevated && <LoadingContent />}
          {isLoading && isElevated && (
            <span className='flex flex-col items-center gap-2 py-8 text-sm text-grey'>
              <CircularProgress size={30} />
              Loading...
            </span>
          )}
          {!isLoading && children}
        </div>

        {footer && !isLoading && !hideFooter && (
          <div className={styles.footer}>{footer}</div>
        )}
      </div>
    </div>,
    document.getElementById('modalPortal')
  );
}

Modal.propTypes = {
  active: propTypes.oneOfType([
    propTypes.bool,
    propTypes.string,
    propTypes.number
  ]),
  closeModal: propTypes.func.isRequired,
  variant: propTypes.oneOf(['default', 'elevated']),
  title: propTypes.node,
  children: propTypes.node,
  footer: propTypes.node,
  size: propTypes.oneOf([
    'auto',
    'small',
    'medium',
    'large',
    'larger',
    'xlarger'
  ]),
  isLoading: propTypes.bool,
  alert: propTypes.bool,
  danger: propTypes.bool,
  hideFooter: propTypes.bool,
  className: propTypes.string,
  showScrollbar: propTypes.bool,
  headerClassName: propTypes.string,
  headerStyle: propTypes.object,
  fullBleedHeader: propTypes.bool,
  cardStyle: propTypes.object,
  cardDataTour: propTypes.string,
  headerTitleClassName: propTypes.string,
  closeButtonClassName: propTypes.string,
  closeButtonStyle: propTypes.object,
  contentRef: propTypes.oneOfType([propTypes.func, propTypes.object])
};
